export interface AvailableSite {
  avatarUrl: string;
  cloudId: string;
  displayName: string;
  isVertigo: boolean;
  products: string[];
  url: string;
}

export type FamilyProductName = 'jira' | 'confluence';

export type SubProductName =
  | 'jira-software'
  | 'jira-core'
  | 'jira-servicedesk'
  | 'confluence'
  | 'jira-product-discovery';

export interface Site {
  name: string;
  url: URL;
  familyProducts: FamilyProductName[];
  subProducts?: SubProductName[];
  cloudId: string;
  iconUrl: URL | null;
}

export interface CloudId {
  cloudId: Site['cloudId'];
}

export type AccessibleProductsRequest = {
  productIds: ProductId[];
  permissionIds: PermissionId[];
};

export interface AccessibleProductsResponse {
  products: Product[];
}

/**
 * Response envelope returned by the `/experimental/v2/accessible-products`
 * endpoint. The v2 endpoint wraps the products payload in a `data` field.
 */
export interface AccessibleProductsResponseV2 {
  data: AccessibleProductsResponse;
}

export interface Product {
  productId: ProductId;
  workspaces: Workspace[];
}

export interface Workspace {
  cloudId: string;
  cloudUrl: string;
  orgId: string;
  workspaceAvatarUrl: string | null;
  workspaceDisplayName: string;
  workspaceUrl: string; // This will have the custom domain
}

export enum ProductId {
  CONFLUENCE = 'confluence.ondemand',
  JIRA_SOFTWARE = 'jira-software.ondemand',
  JIRA_BUSINESS = 'jira-core.ondemand',
  JIRA_SERVICE_DESK = 'jira-servicedesk.ondemand',
  JIRA_PRODUCT_DISCOVERY = 'jira-product-discovery',
  COMPASS = 'compass',
  OPSGENIE = 'opsgenie',
  STATUSPAGE = 'statuspage',
  ATLAS = 'townsquare',
  JIRA_INCIDENT_MANAGER = 'jira-incident-manager.ondemand', // discontinued
  HIPCHAT = 'hipchat.cloud', // discontinued
}

export const availableSiteToSubProductName: Record<
  ProductId,
  SubProductName | undefined
> = {
  [ProductId.JIRA_SOFTWARE]: 'jira-software',
  [ProductId.JIRA_BUSINESS]: 'jira-core',
  [ProductId.JIRA_SERVICE_DESK]: 'jira-servicedesk',
  [ProductId.JIRA_PRODUCT_DISCOVERY]: 'jira-product-discovery',
  [ProductId.CONFLUENCE]: 'confluence',
  // products not used in canvas do not currently map to a sub product name
  [ProductId.COMPASS]: undefined,
  [ProductId.OPSGENIE]: undefined,
  [ProductId.STATUSPAGE]: undefined,
  [ProductId.ATLAS]: undefined,
  // discontinued product types never map to a sub product name
  [ProductId.JIRA_INCIDENT_MANAGER]: undefined,
  [ProductId.HIPCHAT]: undefined,
};

export enum PermissionId {
  WRITE = 'write',
}

export const availableSiteToProductName: Record<
  ProductId,
  FamilyProductName | undefined
> = {
  [ProductId.JIRA_SOFTWARE]: 'jira',
  [ProductId.JIRA_BUSINESS]: 'jira',
  [ProductId.JIRA_SERVICE_DESK]: 'jira',
  [ProductId.JIRA_PRODUCT_DISCOVERY]: 'jira',
  [ProductId.CONFLUENCE]: 'confluence',
  // products not used in canvas do not currently map to a product name
  [ProductId.COMPASS]: undefined,
  [ProductId.OPSGENIE]: undefined,
  [ProductId.STATUSPAGE]: undefined,
  [ProductId.ATLAS]: undefined,
  // diuscointinued product types never map to a product name
  [ProductId.JIRA_INCIDENT_MANAGER]: undefined,
  [ProductId.HIPCHAT]: undefined,
};

export function getAvailableSitesUsedByCanvas(): ProductId[] {
  return Object.entries(availableSiteToProductName)
    .filter(([, value]) => !!value)
    .map(([key]) => key as ProductId);
}

/**
 * Helper function to check if a site has any Jira products (including JPD)
 * Maintains backward compatibility for existing jiraSites filtering
 */
export function siteHasJiraProducts(site: Site): boolean {
  return site.familyProducts.includes('jira');
}

/**
 * Helper function to check if a site has Jira Product Discovery
 */
export function siteHasJiraProductDiscovery(site: Site): boolean {
  return site.subProducts?.includes('jira-product-discovery') ?? false;
}

/**
 * Helper function to check if a site has traditional Jira products (excluding JPD)
 */
export function siteHasTraditionalJira(site: Site): boolean {
  return (
    site.subProducts?.some((subProduct) =>
      ['jira-software', 'jira-core', 'jira-servicedesk'].includes(subProduct),
    ) ?? false
  );
}

/**
 * Helper function to check if a site has a specific sub product
 */
export function siteHasSubProduct(site: Site, subProduct: SubProductName): boolean {
  return site.subProducts?.includes(subProduct) ?? false;
}
